import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { database, storage, auth } from './firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './FirmaBilgileriGuncelle.css';
import { redirectToLanding } from '../utils/landingRedirect';

const FirmaBilgileriGuncelle = ({ userData, onClose, userEmail, onUpdate }) => {
  const [companyName, setCompanyName] = useState('');
  const [companyTitle, setCompanyTitle] = useState('');
  const [address, setAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [logo, setLogo] = useState(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [showLogoUpdatePopup, setShowLogoUpdatePopup] = useState(false);
  const [logoUpdateStep, setLogoUpdateStep] = useState('loading'); // 'loading' -> 'success'
  const [error, setError] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Hesap silme için state'ler
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');
  
  // Puantaj fiyat gösterme ayarı
  const [showPuantajPrice, setShowPuantajPrice] = useState(false);
  
  // Crop özelliği için state'ler
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 100,
    height: 100, // 120x120 ratio için (1:1 kare)
    x: 0,
    y: 0 // Kare olduğu için merkezi
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  // Firebase Functions
  const functions = getFunctions();
  const deleteSelfAccount = httpsCallable(functions, 'deleteSelfAccount');

  useEffect(() => {
    if (userData) {
      setCompanyName(userData.company || userData.companyName || '');
      setCompanyTitle(userData.companyTitle || '');
      setAddress(userData.address || '');
      setPhoneNumber(userData.phoneNumber || '');
      setCurrentLogoUrl(userData.logoUrl || '');
      setShowPuantajPrice(userData.puantajfiyat === 'yes');
    }
  }, [userData]);

  // Canvas'a crop'lanmış resmi çiz (ratio tabanlı, yüksek kalite)
  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!crop || !image) {
      return null;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Crop edilen alanın gerçek boyutları
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;
    
    // Minimum kalite için boyut kontrolü (en az 200px)
    const minSize = 200;
    let outputSize = Math.max(cropWidth, cropHeight, minSize);
    
    // Maksimum kalite için boyut sınırı (en fazla 800px)
    const maxSize = 800;
    if (outputSize > maxSize) {
      outputSize = maxSize;
    }

    canvas.width = outputSize;
    canvas.height = outputSize;

    // Yüksek kalite için canvas ayarları
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      cropWidth,
      cropHeight,
      0,
      0,
      outputSize,
      outputSize
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/png',
        0.95 // Yüksek kalite
      );
    });
  };

  // Crop onaylandığında çalışacak fonksiyon
  const handleCropComplete = async () => {
    if (completedCrop && imgRef.current) {
      try {
        const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
        const croppedFile = new File([croppedBlob], 'logo.png', { type: 'image/png' });
        
        setLogo(croppedFile);
        setLogoPreview(URL.createObjectURL(croppedBlob));
        setShowCropModal(false);
        setError(null);
        
        console.log('🎨 Logo crop tamamlandı:', {
          outputBoyut: `${croppedBlob.size > 0 ? 'Yüksek kalite' : 'Hata'}`,
          dosyaBoyutu: `${(croppedBlob.size / 1024).toFixed(1)} KB`,
          kalite: 'Ratio tabanlı (200-800px arası)'
        });
        
      } catch (error) {
        console.error('Crop işlemi hatası:', error);
        setError('Logo kırpma işlemi sırasında bir hata oluştu.');
      }
    }
  };

  // Crop iptal edildiğinde
  const handleCropCancel = () => {
    setShowCropModal(false);
    setSelectedImage(null);
  };

  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      
      // Dosya boyutu kontrolü (max 5MB - crop öncesi)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo dosyası 5MB\'dan küçük olmalıdır.');
        return;
      }
      
      // Minimum dosya boyutu kontrolü (en az 1KB)
      if (file.size < 1024) {
        setError('Logo dosyası çok küçük. En az 1KB olmalıdır.');
        return;
      }
      
      // Format kontrolü
      if (!file.type.startsWith('image/')) {
        setError('Sadece resim dosyaları yüklenebilir.');
        return;
      }
      
      // Desteklenen formatlar kontrolü
      const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!supportedFormats.includes(file.type.toLowerCase())) {
        setError('Desteklenen formatlar: JPEG, PNG, WebP');
        return;
      }
      
      // Resim boyutları kontrolü için Image objesi oluştur
      const img = new Image();
      img.onload = () => {
        // Minimum resim boyutu kontrolü (en az 100x100px)
        if (img.width < 100 || img.height < 100) {
          setError('Logo en az 100x100 piksel boyutunda olmalıdır.');
          return;
        }
        
        // Maksimum resim boyutu kontrolü (en fazla 4000x4000px)
        if (img.width > 4000 || img.height > 4000) {
          setError('Logo en fazla 4000x4000 piksel boyutunda olmalıdır.');
          return;
        }
        
        // Çok dikdörtgen resimler için uyarı
        const aspectRatio = Math.max(img.width, img.height) / Math.min(img.width, img.height);
        if (aspectRatio > 3) {
          setError('Logo çok dikdörtgen. Daha kare bir resim seçin (en fazla 3:1 oranında).');
          return;
        }
        
        console.log('📷 Logo validasyon başarılı:', {
          boyut: `${img.width}x${img.height}px`,
          dosyaBoyutu: `${(file.size / 1024).toFixed(1)} KB`,
          format: file.type,
          aspectRatio: `${aspectRatio.toFixed(2)}:1`
        });
        
        // Tüm kontroller başarılı - crop modal için resmi hazırla
        const reader = new FileReader();
        reader.onload = () => {
          setSelectedImage(reader.result);
          setShowCropModal(true);
          setError(null);
        };
        reader.readAsDataURL(file);
      };
      
      img.onerror = () => {
        setError('Resim dosyası bozuk veya geçersiz.');
      };
      
      // Resmi yükle (validasyon için)
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  // Toggle değiştiğinde otomatik kaydetme fonksiyonu
  const handlePriceToggleChange = async (newValue) => {
    try {
      console.log('🔄 Fiyat ayarı değiştiriliyor:', newValue);
      
      // Önce state'i güncelle
      setShowPuantajPrice(newValue);
      
      // Firebase'e hemen kaydet
      const userDocRef = doc(database, 'users', userEmail);
      await updateDoc(userDocRef, {
        puantajfiyat: newValue ? 'yes' : 'no',
        updatedAt: new Date().toISOString()
      });
      
      console.log('✅ Fiyat ayarı Firebase\'e kaydedildi:', newValue ? 'yes' : 'no');
      
      // Parent component'e bilgi ver
      if (onUpdate) {
        onUpdate({
          puantajfiyat: newValue ? 'yes' : 'no'
        });
      }
      
    } catch (error) {
      console.error('❌ Fiyat ayarı kaydetme hatası:', error);
      // Hata durumunda state'i geri al
      setShowPuantajPrice(!newValue);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      let logoUrl = currentLogoUrl;

      // Logo güncellemesi varsa yükle
      if (logo) {
        setShowLogoUpdatePopup(true);
        setLogoUpdateStep('loading');

        const logoRef = ref(storage, `logos/${userEmail}`);
        const uploadResult = await uploadBytes(logoRef, logo);
        logoUrl = await getDownloadURL(uploadResult.ref);

        console.log('Logo başarıyla yüklendi:', logoUrl);
      }

      // Kullanıcı verilerini güncelle
      const userDocRef = doc(database, 'users', userEmail);
      const updateData = {
        company: companyName,
        companyName: companyName,
        companyTitle: companyTitle,
        address: address,
        phoneNumber: phoneNumber,
        logoUrl: logoUrl,
        puantajfiyat: showPuantajPrice ? 'yes' : 'no',
        updatedAt: new Date().toISOString()
      };

      await updateDoc(userDocRef, updateData);

      // Setup progress kontrolü - Firma bilgileri tamamlandı mı?
      const isCompanyCompleted = companyName.trim() !== '' && 
                                address.trim() !== '' && 
                                phoneNumber.trim() !== '';

      // Setup progress'i güncelle
      await updateDoc(userDocRef, {
        'setupProgress.company': isCompanyCompleted
      });

      console.log('✅ Firma bilgileri güncellendi:', {
        company: companyName,
        address: address,
        phone: phoneNumber,
        setupCompleted: isCompanyCompleted
      });

      // onUpdate callback'ini çağır
      if (onUpdate) {
        onUpdate({
          ...updateData,
          setupProgress: { company: isCompanyCompleted }
        });
      }

      if (logo) {
        // Logo güncellemesi varsa özel popup göster
        setTimeout(() => {
          setLogoUpdateStep('success');
        }, 2000);

        setTimeout(() => {
          setShowLogoUpdatePopup(false);
          setLogoUpdateStep('loading');
          onClose();
        }, 6000);
      } else {
        // Logo güncellemesi yoksa normal başarı mesajı
        setUpdateSuccess(true);
        setTimeout(() => {
          setUpdateSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error("Firma bilgileri güncellenirken hata oluştu:", error);
      setError("Firma bilgileri güncellenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.");
    }
  };

  // Hesap silme fonksiyonları
  const handleDeleteAccountClick = () => {
    setShowDeleteAccountModal(true);
    setDeleteEmailInput('');
    setDeleteAccountError('');
  };

  const handleDeleteAccountClose = () => {
    setShowDeleteAccountModal(false);
    setDeleteEmailInput('');
    setDeleteAccountError('');
  };

  const handleDeleteAccountConfirm = async () => {
    // Email doğrulama
    if (deleteEmailInput.trim() !== userEmail) {
      setDeleteAccountError('Lütfen hesabınızla ilişkili email adresini doğru şekilde girin.');
      return;
    }

    setDeleteAccountLoading(true);
    setDeleteAccountError('');

    try {
      console.log('Hesap silme işlemi başlatılıyor...');
      
      // Firebase Cloud Function'ı çağır
      const result = await deleteSelfAccount({ email: userEmail });
      
      if (result.data && result.data.success) {
        console.log('Hesap silme işlemi başarılı:', result.data.message);
        
        // Kullanıcıyı çıkış yap ve landing page'e yönlendir
        await signOut(auth);
        redirectToLanding();
        
      } else {
        throw new Error(result.data?.message || 'Hesap silme işlemi başarısız oldu');
      }
    } catch (error) {
      console.error('Hesap silme hatası:', error);
      setDeleteAccountError(`Hesap silinirken hata oluştu: ${error.message}`);
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  return (
    <div className="firma-bilgileri-modal" onClick={onClose}>
      <div className="firma-bilgileri-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-button" onClick={onClose} type="button">
          ✕
        </button>
        <h2>Firma Bilgilerini Güncelle</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <input
              type="text"
              placeholder="Firma Adı *"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
          </div>
          
          <div className="form-row">
            <input
              type="text"
              placeholder="Firma Unvanı (Ltd. Şti., A.Ş. vb.)"
              value={companyTitle}
              onChange={(e) => setCompanyTitle(e.target.value)}
            />
          </div>
          
          <div className="form-row">
            <input
              type="text"
              placeholder="Adres (Mahalle, Sokak, No, İlçe/İl)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
          
          <div className="form-row">
            <input
              type="tel"
              placeholder="Telefon Numarası (0XXX XXX XX XX)"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
          
          <div className="form-row price-settings-row">
            <div className="price-toggle-container">
              <div className="price-toggle-header">
                <span className="price-toggle-title">💰 Puantajlarda fiyat göster</span>
                <div 
                  className={`price-toggle-switch ${showPuantajPrice ? 'active' : ''}`}
                  onClick={() => handlePriceToggleChange(!showPuantajPrice)}
                >
                  <div className="price-toggle-slider"></div>
                </div>
              </div>
              <div className="price-toggle-description">
                {showPuantajPrice ? 
                  "✅ Aktif - Puantaj listesinde ve PDF'lerde fiyat hesaplaması gösterilir" :
                  "❌ Pasif - Sadece saat bilgileri gösterilir, fiyat hesaplanmaz"
                }
              </div>
            </div>
          </div>
          
          <div className="logo-section">
            <label htmlFor="logo-input" className="logo-label">
              📷 Logo Seç (Kare format, yüksek kalite, max 5MB)
            </label>
            <input
              id="logo-input"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="logo-input"
            />
            
            {/* Logo önizlemesi */}
            {logoPreview && (
              <div className="logo-preview">
                <p>✨ Yeni Logo:</p>
                <img src={logoPreview} alt="Logo Preview" className="preview-logo" />
              </div>
            )}
            
            {/* Mevcut logo */}
            {currentLogoUrl && !logoPreview && (
              <div className="current-logo-section">
                <p>📋 Mevcut Logo:</p>
                <img src={currentLogoUrl} alt="Current Logo" className="current-logo" />
              </div>
            )}
          </div>
          
          <div className="button-group">
            <button type="submit">✅ Güncelle</button>
          </div>
        </form>
        
        {/* Hesap Silme Butonu */}
        <div className="danger-zone">
          <h3>⚠️ Tehlikeli Alan</h3>
          <p>Bu işlem geri alınamaz. Hesabınız ve tüm verileriniz kalıcı olarak silinecektir.</p>
          <button 
            type="button" 
            className="delete-account-button"
            onClick={handleDeleteAccountClick}
          >
            🗑️ Hesabımı Sil
          </button>
        </div>

        {updateSuccess && (
          <div className="update-success">
            <span className="checkmark">&#10004;</span> Başarıyla güncellendi
          </div>
        )}

        {/* Hesap Silme Modal'ı */}
        {showDeleteAccountModal && (
          <div className="delete-account-modal-overlay">
            <div className="delete-account-modal">
              <div className="modal-header">
                <h2>⚠️ Hesabı Sil</h2>
              </div>
              <div className="modal-content">
                <div className="warning-message">
                  <p><strong>DİKKAT:</strong> Bu işlem geri alınamaz!</p>
                  <p>Hesabınızı sildiğinizde:</p>
                  <ul>
                    <li>✗ Tüm müşteri bilgileriniz silinecek</li>
                    <li>✗ Tüm puantaj kayıtlarınız silinecek</li>
                    <li>✗ Tüm operatör bilgileriniz silinecek</li>
                    <li>✗ Tüm makine kayıtlarınız silinecek</li>
                    <li>✗ Firma bilgileriniz silinecek</li>
                    <li>✗ Hesabınıza bir daha erişemeyeceksiniz</li>
                  </ul>
                </div>
                
                <div className="email-verification">
                  <label>Bu işlemi onaylamak için email adresinizi girin:</label>
                  <input
                    type="email"
                    placeholder="Email adresinizi girin"
                    value={deleteEmailInput}
                    onChange={(e) => setDeleteEmailInput(e.target.value)}
                    className="email-input"
                  />
                  <p className="current-email">Mevcut email: {userEmail}</p>
                </div>

                {deleteAccountError && (
                  <div className="error-message">
                    {deleteAccountError}
                  </div>
                )}

                <div className="modal-buttons">
                  <button
                    onClick={handleDeleteAccountClose}
                    className="cancel-button"
                    disabled={deleteAccountLoading}
                  >
                    ❌ İptal
                  </button>
                  <button
                    onClick={handleDeleteAccountConfirm}
                    className="confirm-delete-button"
                    disabled={deleteAccountLoading || deleteEmailInput.trim() !== userEmail}
                  >
                    {deleteAccountLoading ? '🔄 Siliniyor...' : '🗑️ Silmeyi Onaylıyorum'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logo Güncelleme Popup */}
        {showLogoUpdatePopup && (
          <div className="logo-update-popup-overlay">
            <div className="logo-update-popup">
              <div className="popup-content">
                {logoUpdateStep === 'loading' ? (
                  <>
                    <div className="spinner-container">
                      <div className="green-spinner"></div>
                    </div>
                    <div className="popup-text">
                      <h3>Firma bilgileri güncelleniyor...</h3>
                      <p><strong>Not:</strong> Logo güncellemesi 5 dakika kadar sonra puantaj düzenlemesine yansıyacaktır.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="success-icon">
                      <div className="checkmark-circle">
                        <span className="checkmark">&#10004;</span>
                      </div>
                    </div>
                    <div className="popup-text">
                      <h3>Başarıyla güncellendi!</h3>
                      <p>Firma bilgileriniz başarıyla kaydedildi.</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Logo Crop Modal */}
      {showCropModal && (
        <div className="crop-modal-overlay">
          <div className="crop-modal-content">
            <h3>Logo Kırpma</h3>
            <p>Logoyu kare formatta ayarlayın - Yüksek kalitede işlenecek</p>
            
            <div className="crop-container">
              {selectedImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(newCrop) => setCrop(newCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1} // 1:1 ratio (kare)
                  minWidth={60}
                  minHeight={60}
                  keepSelection={true}
                  disabled={false}
                  locked={false}
                >
                  <img
                    ref={imgRef}
                    src={selectedImage}
                    alt="Crop"
                    className="crop-image"
                    onLoad={() => {
                      // İlk yüklemede merkezi crop ayarla
                      const { width, height } = imgRef.current;
                      const aspectRatio = 1; // 1:1 kare ratio
                      
                      let cropWidth, cropHeight;
                      if (width / height > aspectRatio) {
                        cropHeight = height;
                        cropWidth = height * aspectRatio;
                      } else {
                        cropWidth = width;
                        cropHeight = width / aspectRatio;
                      }
                      
                      const newCrop = {
                        unit: 'px',
                        width: cropWidth,
                        height: cropHeight,
                        x: (width - cropWidth) / 2,
                        y: (height - cropHeight) / 2,
                      };
                      
                      setCrop(newCrop);
                      setCompletedCrop(newCrop);
                    }}
                  />
                </ReactCrop>
              )}
            </div>
            
            <div className="crop-actions">
              <button 
                onClick={handleCropComplete}
                className="crop-confirm-btn"
                disabled={!completedCrop}
              >
                ✓ Onayla
              </button>
              <button 
                onClick={handleCropCancel}
                className="crop-cancel-btn"
              >
                ✗ İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirmaBilgileriGuncelle;